// Libraries
import type { CustomerCardOptions, CustomerCardState, Dealership } from '@makemydeal/dr-sales-checkin-types';

// Selectors
import { getActiveDeal } from '../activeDeal/selector';

// Actions
import { Actions } from '../actionTypes/actionTypes';

// Types
import type { OpenDealPayload } from './types/common';

export const fetchInitialConfigAction = (dealerMMDId: string) => {
    return {
        type: Actions.DEALER_DETAILS,
        payload: { dealerMMDId }
    };
};

export const setCustomerCardStateAction = (customerCardState: CustomerCardState, options?: CustomerCardOptions) => {
    return {
        type: Actions.SET_CUSTOMER_CARD_STATE,
        payload: { customerCardState, options }
    };
};

export const handleGlobalErrorAction = (message: string) => {
    return {
        type: Actions.GLOBAL_ERROR,
        payload: { message }
    };
};

const setCustomerVehiclesSlideoutVisibilityAction = (isVisible: boolean) => ({
    type: Actions.SET_CUSTOMER_VEHICLES_SLIDEOUT_VISIBILITY,
    payload: isVisible
});

// Duplicates of the slideout action creators owned by activities-customer-vehicles
// (src/store/actionCreators.ts) — redeclared here so this store can drive that activity's
// internal state without importing the activity.
const showCustomerVehiclesSlideout = (customerFirstName?: string) => ({
    type: Actions.SHOW_CUSTOMER_VEHICLES_SLIDEOUT,
    payload: { customerFirstName }
});

const hideCustomerVehiclesSlideout = () => ({
    type: Actions.HIDE_CUSTOMER_VEHICLES_SLIDEOUT
});

export const setCustomerVehiclesSlideoutVisibility = (isVisible: boolean) => {
    return (dispatch: (action: unknown) => void, getState?: () => any) => {
        // Notify the consuming app of the visibility change
        dispatch(setCustomerVehiclesSlideoutVisibilityAction(isVisible));

        // Sync to the activities-customer-vehicles internal Redux state
        if (isVisible) {
            const state = getState?.() ?? {};
            const customerFirstName = getActiveDeal(state).dealStructure?.participants?.buyer?.firstName;
            dispatch(showCustomerVehiclesSlideout(customerFirstName));
        } else {
            dispatch(hideCustomerVehiclesSlideout());
        }
    };
};

const setDealListCompareEnabledAction = (enabled: boolean) => ({
    type: Actions.SET_DEAL_LIST_COMPARE_ENABLED,
    payload: enabled
});

export const setDealListCompareEnabled = (enabled: boolean) => {
    return (dispatch: (action: unknown) => void) => {
        // Dispatch to the customer vehicles package's Redux state to control compare checkbox visibility
        // This action is handled by customerVehiclesReducer from @makemydeal/activities-customer-vehicles package
        // The reducer is registered in src/shells/main/activities/customerVehicles/index.ts
        dispatch(setDealListCompareEnabledAction(enabled));
    };
};

export const cdlv3DataReady = () => {
    return {
        type: Actions.CDLV3_DATA_READY,
        payload: {}
    };
};

export const updateDealerDetails = (data: Dealership) => {
    return {
        type: Actions.UPDATE_DEALER_DETAILS,
        payload: { data }
    };
};

export const updateStateCustomer = (customer: any) => {
    return {
        type: Actions.UPDATE_CURRENT_CUSTOMER,
        payload: {
            customer
        }
    };
};

/**
 * Pre-populates the customer and vehicle meta slices so that the subsequent
 * `updateDealAction` middleware handler can read them via `getState()`.
 *
 * This action is intentionally NOT handled by the middleware — it only
 * updates the reducers, avoiding a duplicate / stale-state API call.
 */
export const prepareDealSetupState = (customer: any, selectedDxgVehicle: any) => {
    return {
        type: Actions.PREPARE_DEAL_SETUP_STATE,
        payload: { customer, selectedDxgVehicle }
    };
};

export const setDealerLogo = (logoURL: string) => {
    return {
        type: Actions.SET_DEALER_LOGO,
        payload: { logoURL }
    };
};

export const openDealFromCustomerVehicles = (payload: OpenDealPayload) => {
    return {
        type: Actions.OPEN_DEAL_FROM_CUSTOMER_VEHICLES,
        payload
    };
};

export const openCompareDealPage = () => {
    return {
        type: Actions.OPEN_COMPARE_DEAL_PAGE
    };
};

export const setShowroomLeftNavOpen = (isOpen: boolean) => {
    return {
        type: Actions.SET_SHOWROOM_LEFT_NAV_OPEN,
        payload: isOpen
    };
};

export const fetchCustomerVehicleDealsRequest = () => ({
    type: Actions.CUSTOMER_VEHICLES_FETCH_DEALS_REQUEST
});

export const fetchCustomerVehicleDealsSuccess = (deals: any[]) => ({
    type: Actions.CUSTOMER_VEHICLES_FETCH_DEALS_SUCCESS,
    payload: { deals }
});

export const fetchCustomerVehicleDealsFailure = (error: string) => ({
    type: Actions.CUSTOMER_VEHICLES_FETCH_DEALS_FAILURE,
    payload: error
});

export const closeTradeInModal = () => ({
    type: Actions.CLOSE_TRADE_IN_MODAL
});

export const creditConsentClicked = (payload: { checked: boolean; role: string }) => ({
    type: Actions.CREDIT_CONSENT_CLICKED,
    payload
});

export const continueToApplyDeal = (vinIds: string[]) => ({
    type: Actions.CONTINUE_TO_APPLY_DEAL,
    payload: vinIds
});
